/*
 * Where an agent thread opens. Mirrors `thread_path` in the backend's
 * `case_agent/refs.py`; keep the two in sync.
 *
 *   /cases/:id/workspace?thread=…                     a case thread (Case IDE)
 *   /clients/:clientId?agent=client[&thread=…]        a client thread (panel)
 *   /home?agent=practice[&thread=…]                   a practice thread (panel)
 *
 * No React or IO here so the rules stay unit-testable.
 */
import { HOME_PATH } from "@/features/dashboard/routes";

import type { AgentScope } from "./types";

export const AGENT_PARAM = "agent";
export const THREAD_PARAM = "thread";
export const MESSAGE_PARAM = "message";

const enc = encodeURIComponent;

/**
 * Router state for a link that opens a thread on purpose (the Agents menu,
 * an alert): the Case IDE shows its chat even if it was hidden. A reload or
 * a tab switch, which carry the same `?thread=`, does not.
 */
export const OPEN_THREAD_STATE = { openThread: true } as const;

export const opensThread = (state: unknown): boolean =>
  typeof state === "object" &&
  state !== null &&
  (state as { openThread?: unknown }).openThread === true;

/**
 * Where a thread opens: the Case IDE for a case, otherwise a page with the
 * side panel deep-linked to the thread (the client's profile, or Home for the
 * practice).
 */
export const panelThreadPath = (
  scope: Pick<AgentScope, "kind" | "id">,
  threadId: string
): string => {
  if (scope.kind === "case")
    return `/cases/${enc(scope.id)}/workspace?${THREAD_PARAM}=${enc(threadId)}`;
  const params = new URLSearchParams({
    [AGENT_PARAM]: scope.kind === "client" ? "client" : "practice",
    [THREAD_PARAM]: threadId
  });
  const base =
    scope.kind === "client" ? `/clients/${enc(scope.id)}` : HOME_PATH;
  return `${base}?${params.toString()}`;
};
